import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Download } from 'lucide-react';
import { _t } from '@/i18n/t';
import { APP_ROUTES } from '@/app/routePaths';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import ContactModal from '@/features/site-health/components/ContactModal';
import { cn } from '@/shared/lib/utils';

import './chrome.css';

interface TopbarProps {
  onStartOver?: () => void;
  onWalkthrough?: () => void;
  showWalkthrough?: boolean;
  quiet?: boolean;
  primaryAction?: { label: string; onClick: () => void };
  onDownloadJson?: () => void;
  onDebug?: () => void;
}

export default function Topbar({ onStartOver, onWalkthrough, showWalkthrough = true, quiet = false, primaryAction, onDownloadJson, onDebug }: TopbarProps) {
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <div className="sticky top-0 w-full h-14 flex items-center justify-between px-8 z-10 bg-transparent">
        <div className="flex items-center">
          <button
            type="button"
            className="ob-topbar__home"
            onClick={() => navigate(APP_ROUTES.DASHBOARD)}
            aria-label={_t('onboarding.topbar.home')}
            data-testid="onboarding-topbar-home"
          >
            <img src="/images/logo_trans.png" alt="TypeHero" className="h-6 w-auto" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          {onDebug && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={onDebug}
              aria-label={_t('onboarding.factsDebug.button')}
              title={_t('onboarding.factsDebug.button')}
              data-testid="onboarding-topbar-facts-debug"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
            >
              <Bug className="w-4 h-4" />
            </button>
          )}
          {onDownloadJson && (
            <button
              type="button"
              className="bg-black text-white hover:opacity-80 transition-opacity flex items-center gap-1.5"
              style={{ fontSize: 12, border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
              onClick={onDownloadJson}
              data-th-button="true"
            >
              <Download className="w-4 h-4" />
              Download JSON
            </button>
          )}
          {showWalkthrough && (
            <button
              type="button"
              className={cn(
                'hover:text-foreground transition-colors',
                quiet
                  ? 'text-[12.5px] text-[hsl(var(--onboarding-chrome-muted))]'
                  : 'text-[14px] text-[hsl(var(--onboarding-ink-2))] underline decoration-1 underline-offset-[3px]',
              )}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={onWalkthrough ?? (() => setShowContactModal(true))}
            >
              {_t('onboarding.topbar.walkthrough')}
            </button>
          )}
          {onStartOver && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors"
              style={{ fontSize: 12, border: '1px solid hsl(var(--border))', borderRadius: 6, padding: '5px 10px', background: 'transparent', cursor: 'pointer' }}
              onClick={() => setShowStartOverConfirm(true)}
            >
              {_t('onboarding.wrongBusiness.button')}
            </button>
          )}
          {primaryAction && (
            <button
              type="button"
              className="ob-topbar__primary bg-foreground/[0.03] hover:bg-foreground/[0.06] border border-foreground/20 hover:border-foreground/30 text-foreground transition-colors"
              style={{ fontSize: 14, fontWeight: 500, borderRadius: 6, padding: '10px 20px', cursor: 'pointer' }}
              onClick={primaryAction.onClick}
              data-th-button="true"
            >
              {primaryAction.label}
            </button>
          )}
        </div>
      </div>

      {onStartOver && (
        <ConfirmDialog
          open={showStartOverConfirm}
          onOpenChange={setShowStartOverConfirm}
          onConfirm={() => onStartOver()}
          title={_t('onboarding.wrongBusiness.confirmTitle')}
          description={_t('onboarding.wrongBusiness.confirmDesc')}
          confirmLabel={_t('onboarding.wrongBusiness.confirmLabel')}
          cancelLabel={_t('onboarding.wrongBusiness.dismissLabel')}
          confirmVariant="danger"
          elevated
        />
      )}

      <ContactModal open={showContactModal} onClose={() => setShowContactModal(false)} source="onboarding_walkthrough" />
    </>
  );
}
